import React, { useEffect, useMemo, useRef } from 'react';
import { useField, useFormState } from 'react-final-form';
import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client';

import PrimaryButton, { SecondaryButton } from '@common/button';
import { useAutoscroll } from '@common/utils/hooks/useAutoScroll';
import { useScopedTranslation } from '@common/utils/hooks/useScopedTranslation';
import { ContactsQuery } from '@typings/api';
import { times } from '@utils/array';

import Section from '../_common/Section';
import SectionHeader from '../_common/SectionHeader';
import { MainFormFields } from '../BookingFlow';
import { CONTACTS } from '../queries';

import { Contact } from './Contact';
import { ContactSkeleton } from './ContactSkeleton';

const CONTACTS_FETCH_LIMIT = 6;

export function Contacts() {
  const { t } = useScopedTranslation();
  const container = useRef<HTMLDivElement>(null);
  const {
    values: { contact: selectedContact, listingId, offices, subjectId },
  } = useFormState();
  const officeId = offices[0].id;

  const {
    input: { onChange },
  } = useField(MainFormFields.CONTACT);

  const { data, fetchMore, loading, networkStatus } = useQuery<ContactsQuery>(
    CONTACTS,
    {
      variables: {
        officeId,
        subjectId,
        listingId,
        limit: CONTACTS_FETCH_LIMIT,
        offset: 0,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const contacts = useMemo(
    () =>
      networkStatus === NetworkStatus.setVariables
        ? []
        : data?.contacts.contacts ?? [],
    [data, networkStatus],
  );

  const totalSkeletons = Math.abs(
    CONTACTS_FETCH_LIMIT - (contacts.length % CONTACTS_FETCH_LIMIT),
  );
  const shouldShowLoadMore =
    data !== undefined && data.contacts.currentPage !== data.contacts.lastPage;
  const shouldShowContinue =
    data !== undefined && data.contacts.contacts.length > 1;

  function handleFetchMore() {
    if (fetchMore !== undefined) {
      fetchMore({
        variables: {
          officeId,
          limit: CONTACTS_FETCH_LIMIT,
          offset: contacts.length,
        },
        updateQuery: (cache, { fetchMoreResult }) => {
          if (fetchMoreResult !== undefined && cache !== undefined) {
            return {
              ...cache,
              contacts: {
                ...cache.contacts,
                contacts: [
                  ...(cache.contacts?.contacts ?? []),
                  ...fetchMoreResult.contacts.contacts,
                ],
              },
            };
          }

          return cache;
        },
      });
    }
  }

  useAutoscroll(container);

  useEffect(() => {
    if (contacts.length === 1) {
      onChange(contacts[0]);
    }
  }, [onChange, contacts]);

  return (
    <Section ref={container}>
      <SectionHeader>{t('main_form.contacts.header')}</SectionHeader>
      <div className="flex flex-wrap items-stretch -m-2">
        {contacts.map((contact) => (
          <div
            className="flex p-4 w-full 2xl:max-w-lg sm:w-1/2 xl:w-1/3"
            key={contact.id}
          >
            <Contact
              contact={contact}
              isSelected={selectedContact?.id === contact.id}
              onSelect={onChange}
            />
          </div>
        ))}
        {loading &&
          times(totalSkeletons).map((idx) => (
            <div
              className="flex p-4 w-full 2xl:max-w-lg sm:w-1/2 xl:w-1/3"
              key={idx}
            >
              <ContactSkeleton />
            </div>
          ))}
      </div>
      <div className="flex mt-4 w-full space-x-4 md:w-auto">
        {shouldShowLoadMore && (
          <SecondaryButton
            disabled={loading}
            onClick={handleFetchMore}
            type="button"
          >
            {t('main_form.contacts.load_more')}
          </SecondaryButton>
        )}
        {shouldShowContinue && (
          <PrimaryButton
            disabled={loading}
            onClick={() => onChange(null)}
            style={{ width: 'auto' }}
            type="button"
          >
            {t('main_form.contacts.continue')}
          </PrimaryButton>
        )}
      </div>
    </Section>
  );
}
